import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSectionNav } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];
const LINES = ["Custom Websites", "for Ambitious", "Businesses."];

export const Hero = () => {
  const ref = useRef(null);
  const go = useSectionNav();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [3.5, -3.5]), { stiffness: 60, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-4.5, 4.5]), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const resetMouse = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-24"
    >
      <div className="absolute inset-0 max-w-7xl mx-auto pointer-events-none hidden sm:grid grid-cols-4" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="border-l border-white/[0.04]" />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 lg:gap-8 items-center w-full">
        <motion.div style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-neutral-500" aria-hidden="true" />
            <span className="mono text-[11px] tracking-[0.28em] text-neutral-400 uppercase">
              Modern Websites. Real Results.
            </span>
          </motion.div>

          <h1 className="font-display font-bold tracking-tight text-neutral-50 text-4xl sm:text-5xl lg:text-6xl leading-[1.06]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.3 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-md text-neutral-400 text-base sm:text-lg leading-relaxed"
          >
            At Novo Digital Studio, we design and build modern websites that help businesses look better online,
            perform stronger and grow faster.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-start-project-btn"
              onClick={() => go("/contact")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-neutral-50 text-[#0a0a0b] font-medium pl-7 pr-6 py-3.5 hover:bg-white transition-colors duration-300"
            >
              Start Your Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-view-work-btn"
              onClick={() => go("/work")}
              className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 text-neutral-200 font-medium px-7 py-3.5 hover:border-white/40 hover:bg-white/5 transition-colors duration-300"
            >
              View Our Work
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
          className="relative [perspective:1200px]"
          onMouseMove={onMouse}
          onMouseLeave={resetMouse}
        >
          <motion.div style={{ rotateX, rotateY, y: yImg, scale: scaleImg }} className="relative">
            <motion.div animate={{ y: [0, -12, 0] }} transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}>
              <img
                src="/assets/hero-devices.png"
                alt="Rise Learning — a website designed and built by Novo, shown on a laptop and smartphone"
                data-testid="hero-device-mockup"
                className="w-full h-auto select-none scale-[1.25] lg:scale-[1.55] lg:translate-x-10"
                draggable="false"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-3"
        aria-hidden="true"
      >
        <span className="mono text-[10px] tracking-[0.3em] text-neutral-600">SCROLL</span>
        <div className="w-px h-10 bg-white/10 overflow-hidden">
          <motion.div
            className="w-full h-1/2 bg-neutral-400"
            animate={{ y: [-20, 40] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};
